import type { CommitTx, DocRow, Driver, FailedOp, Op } from '../types'
import { splitPath } from '../util'

export interface IdbDriverOptions {
  name?: string
  indexedDB?: IDBFactory
}

interface DocRec extends DocRow {
  parent: string
  collection: string
}
interface OutboxRec {
  seq?: number
  opId: string
  op: Op
  error: string | null
}

const DOCS = 'docs'
const OUTBOX = 'outbox'
const KV = 'kv'
const ALL = [DOCS, OUTBOX, KV]

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error)
  })
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onabort = () => reject(tx.error ?? new Error('idb driver: transaction aborted'))
    // A failed request bubbles here before the abort; its error is the useful one.
    tx.onerror = (e) => reject((e.target as IDBRequest | null)?.error ?? tx.error ?? new Error('idb driver: transaction failed'))
  })
}

const toRow = (r: DocRec): DocRow => ({ path: r.path, server: r.server, view: r.view })

export function idbDriver(opts: IdbDriverOptions = {}): Driver {
  const name = opts.name ?? 'battleops'
  let db: IDBDatabase | null = null

  const need = (): IDBDatabase => {
    if (!db) throw new Error('idb driver: init() has not run')
    return db
  }

  return {
    async init() {
      if (db) return
      const factory = opts.indexedDB ?? (globalThis as { indexedDB?: IDBFactory }).indexedDB
      if (!factory) throw new Error('idb driver: IndexedDB is not available')
      const open = factory.open(name, 1)
      open.onupgradeneeded = () => {
        const d = open.result
        d.createObjectStore(DOCS, { keyPath: 'path' }).createIndex('byCollection', ['parent', 'collection'])
        d.createObjectStore(OUTBOX, { keyPath: 'seq', autoIncrement: true }).createIndex('byOpId', 'opId', { unique: true })
        d.createObjectStore(KV)
      }
      db = await req(open)
    },

    async loadAll({ excludeCollections }) {
      const rows = await req(need().transaction(DOCS).objectStore(DOCS).getAll() as IDBRequest<DocRec[]>)
      return rows.filter((r) => !excludeCollections.includes(r.collection)).map(toRow)
    },

    async loadCollection(parentPath, collection) {
      const index = need().transaction(DOCS).objectStore(DOCS).index('byCollection')
      return (await req(index.getAll([parentPath, collection]) as IDBRequest<DocRec[]>)).map(toRow)
    },

    async loadDocs(paths) {
      const store = need().transaction(DOCS).objectStore(DOCS)
      const rows = await Promise.all(paths.map((p) => req(store.get(p) as IDBRequest<DocRec | undefined>)))
      return rows.filter((r): r is DocRec => r !== undefined).map(toRow)
    },

    // Every request is queued synchronously on one transaction, so it commits or aborts as a unit.
    async commit(c: CommitTx) {
      const tx = need().transaction(ALL, 'readwrite')
      const finished = done(tx)
      const docs = tx.objectStore(DOCS)
      const outbox = tx.objectStore(OUTBOX)
      const kv = tx.objectStore(KV)
      const byOpId = outbox.index('byOpId')

      for (const d of c.putDocs) {
        const s = splitPath(d.path)
        docs.put({ path: d.path, parent: s.parent, collection: s.collection, server: d.server, view: d.view } satisfies DocRec)
      }
      for (const p of c.deleteDocs) docs.delete(p)
      for (const op of c.appendOutbox) outbox.add({ opId: op.opId, op, error: null } satisfies OutboxRec)
      for (const id of c.ackOutbox) {
        const r = byOpId.getKey(id)
        r.onsuccess = () => {
          if (r.result !== undefined) outbox.delete(r.result)
        }
      }
      for (const f of c.failOutbox ?? []) {
        const r = byOpId.get(f.opId) as IDBRequest<OutboxRec | undefined>
        r.onsuccess = () => {
          if (r.result) outbox.put({ ...r.result, error: f.error })
        }
      }
      for (const [k, v] of Object.entries(c.kv ?? {})) {
        if (v === null) kv.delete(k)
        else kv.put(v, k)
      }
      await finished
    },

    async readOutbox(limit) {
      const store = need().transaction(OUTBOX).objectStore(OUTBOX)
      return new Promise<Op[]>((resolve, reject) => {
        const out: Op[] = []
        const cur = store.openCursor()
        cur.onerror = () => reject(cur.error)
        cur.onsuccess = () => {
          const c = cur.result
          if (!c || out.length >= limit) return resolve(out)
          const rec = c.value as OutboxRec
          if (rec.error === null) out.push(rec.op)
          c.continue()
        }
      })
    },

    async readFailed() {
      const rows = await req(need().transaction(OUTBOX).objectStore(OUTBOX).getAll() as IDBRequest<OutboxRec[]>)
      return rows.filter((r) => r.error !== null).map((r) => ({ ...r.op, error: r.error ?? '' }) as FailedOp)
    },

    async outboxCount() {
      const rows = await req(need().transaction(OUTBOX).objectStore(OUTBOX).getAll() as IDBRequest<OutboxRec[]>)
      const failed = rows.filter((r) => r.error !== null).length
      return { pending: rows.length - failed, failed }
    },

    async kvGet(key) {
      const v = await req(need().transaction(KV).objectStore(KV).get(key) as IDBRequest<string | undefined>)
      return v ?? null
    },

    async wipe() {
      const tx = need().transaction(ALL, 'readwrite')
      const finished = done(tx)
      for (const s of ALL) tx.objectStore(s).clear()
      await finished
    },
  }
}
